/* eslint-disable */
import React, { useRef, useState, useEffect } from 'react';
import polygon from '../../images/icons/polygon.svg';
import closeIcon from '../../images/icons/delete.svg';
import uploadFileIcon from '../../images/icons/upload-plus-icon.svg';
import fileUpload from '../../images/icons/fileUpload.svg';
import getPoAttachments from './queries/getPoAttachments';
import uploadPoAttachment from './queries/uploadPoAttachment';
import deletePoAttachment from './queries/deletePoAttachment';
import getAgencyPOConfig from './queries/getAgencyPOConfig';
import showAlert from '../../utils/alert';
import Modal from 'react-responsive-modal';

const humanFileSize = (size) => {
  if (!size) return '';
  const i = Math.floor(Math.log(size) / Math.log(1024));
  return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'KB', 'MB', 'GB'][i];
};

const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB per file

/**
 * Single component for both PO Attachments and Advance Request (ARQ) attachments.
 * - attachmentType 'po': list from getPoAttachments(poId), upload/delete use PO APIs.
 * - attachmentType 'arq': list from values (row arqAttachments), same upload API with poId + arqId.
 */
const PoAttachments = ({
  name = 'attachments',
  values = {},
  setFieldValue,
  disabled = false,
  showHeader = true,
  headerTitle = 'PO - Attachments',
  showPreview = true,
  showAddButton = true,
  showDeleteButton = true,
  poId = null,
  arqId = '',
  attachmentType = 'po', // 'po' | 'arq'
  companyId = null,
  expenseCategoryId = null,
  onUploadSuccess
}) => {
  const fileInputRef = useRef(null);
  const formAttachmentsRef = useRef([]);
  const [open, setOpen] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const formAttachments = values?.[name] || [];
  formAttachmentsRef.current = formAttachments;

  const isArq = attachmentType === 'arq';
  const hasPoId = !!poId;
  const agencyPOConfigParams = isArq ? {} : { companyId, expenseCategoryId };
  const agencyPOConfigEnabled = !isArq && companyId != null && companyId !== '' && expenseCategoryId != null && expenseCategoryId !== '';
  const { data: agencyPOConfigResponse } = getAgencyPOConfig(agencyPOConfigParams, { enabled: agencyPOConfigEnabled });
  const agencyPOConfig = (() => {
    const raw = agencyPOConfigResponse?.data;
    if (raw && typeof raw === 'object' && 'isAdvanceRequestAvailable' in raw) return raw;
    if (raw?.data && typeof raw.data === 'object') return raw.data;
    return null;
  })();
  const isConfigReady = !!agencyPOConfig;
  // Enable: edit mode (hasPoId), or PO create when config ready, or ARQ create (no poId)
  const attachmentsSectionEnabled =
    hasPoId ||
    (attachmentType === 'po' && agencyPOConfigEnabled && isConfigReady) ||
    (isArq && !hasPoId);
  const effectiveDisabled = (hasPoId && !isArq)
    ? !attachmentsSectionEnabled
    : (disabled || !attachmentsSectionEnabled);
  const { data: serverAttachmentsData, isLoading: isLoadingAttachments } = getPoAttachments(
    poId,
    isArq ? arqId : null,
    { enabled: !isArq }
  );
  const { mutate: uploadAttachment, isLoading: isUploading } = uploadPoAttachment();
  const { mutate: removeAttachmentApi, isLoading: isDeleting } = deletePoAttachment();

  const rawResponse = serverAttachmentsData?.data;
  const serverList = (() => {
    if (Array.isArray(rawResponse)) return rawResponse;
    if (rawResponse && Array.isArray(rawResponse.data)) return rawResponse.data;
    if (rawResponse && Array.isArray(rawResponse.result)) return rawResponse.result;
    if (rawResponse && rawResponse.data !== undefined && Array.isArray(rawResponse.data)) return rawResponse.data;
    return [];
  })();
  const hasServerList = serverList.length > 0;
  const formHasValues = Array.isArray(formAttachments) && formAttachments.length > 0;
  const getAttachmentId = (a) => a?.docId ?? a?.poAttachmentId ?? a?.attachmentId ?? a?.id;
  const getAttachmentName = (a) => a?.attachmentName ?? a?.fileName ?? a?.name ?? '';
  const getAttachmentSize = (a) => {
    const s = a?.fileSize ?? a?.size;
    if (typeof s === 'number' && !Number.isNaN(s)) return s;
    if (typeof s === 'string') {
      const num = parseFloat(s.replace(/[^\d.]/g, ''), 10);
      return Number.isNaN(num) ? 0 : num;
    }
    return 0;
  };
  const getPreSignedUrl = (a) => a?.preSignedUrl ?? a?.preSignedURL ?? '';
  const attachments = formHasValues ? formAttachments : (hasServerList ? serverList.map((a) => {
    const id = getAttachmentId(a);
    return {
      id,
      attachmentId: id,
      name: getAttachmentName(a),
      size: getAttachmentSize(a),
      preSignedUrl: getPreSignedUrl(a)
    };
  }) : []);

  useEffect(() => {
    if (poId && hasServerList && !formHasValues) {
      const normalized = serverList.map((a) => {
        const id = getAttachmentId(a);
        return {
          id,
          attachmentId: id,
          name: getAttachmentName(a),
          size: getAttachmentSize(a),
          preSignedUrl: getPreSignedUrl(a)
        };
      });
      setFieldValue(name, normalized);
    }
  }, [poId, hasServerList, formHasValues]);

  useEffect(() => {
    return () => {
      (formAttachmentsRef.current || []).forEach((a) => a?.preview && URL.revokeObjectURL(a.preview));
    };
  }, []);

  const normalizeUploadResponse = (res, selectedFiles = []) => {
    const data = res?.data?.data ?? res?.data ?? res;
    const raw = data;
    if (Array.isArray(raw?.arqAttachmentIds) && raw.arqAttachmentIds.length > 0) {
      return raw.arqAttachmentIds.map((id, idx) => {
        const file = selectedFiles[idx];
        return {
          id,
          attachmentId: id,
          name: file?.name ?? '',
          size: file?.size ?? 0
        };
      });
    }
    if (Array.isArray(raw?.poAttachmentIds) && raw.poAttachmentIds.length > 0) {
      return raw.poAttachmentIds.map((id, idx) => {
        const file = selectedFiles[idx];
        return { id, attachmentId: id, name: file?.name ?? '', size: file?.size ?? 0 };
      });
    }
    const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    return list.map((item, idx) => {
      const file = selectedFiles[idx];
      return {
        id: item?.id ?? item?.attachmentId ?? item?.docId ?? `${Date.now()}-${idx}`,
        attachmentId: item?.id ?? item?.attachmentId ?? item?.docId,
        name: item?.fileName ?? item?.name ?? file?.name,
        size: item?.fileSize ?? item?.size ?? file?.size
      };
    });
  };

  const allowedMimeTypes = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/csv',
    'text/plain', // .txt, text
    // Outlook Message Format (.msg) — IANA-style type (see also isOutlookMsgFile fallback)
    'application/vnd.ms-outlook'
  ];

  /** .msg files often report empty type or application/octet-stream in the file picker */
  const isOutlookMsgFile = (file) => (file?.name || '').toLowerCase().endsWith('.msg');

  const isAllowedAttachmentType = (file) => {
    if (allowedMimeTypes.includes(file.type)) return true;
    if (!isOutlookMsgFile(file)) return false;
    const t = file.type || '';
    return (
      t === ''
      || t === 'application/octet-stream'
      || t === 'application/vnd.ms-outlook'
      || t === 'application/x-msmsg'
    );
  };

  const onFilesSelected = (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files).filter((file) => isAllowedAttachmentType(file));
    if (fileList.length === 0) return;

    const oversized = fileList.filter((file) => file.size > MAX_ATTACHMENT_SIZE_BYTES);
    if (oversized.length > 0) {
      const names = oversized.map((f) => `${f.name} (${humanFileSize(f.size)})`).join(', ');
      showAlert(
        `Maximum file size is 5 MB. The following file(s) exceed the limit: ${names}`,
        'error'
      );
      return;
    }

    if (fileList.length > 0) {
      uploadAttachment(
        { files: fileList, purchaseOrderId: poId ?? '', arqId: arqId ?? '' },
        {
          onSuccess: (res) => {
            const newItems = normalizeUploadResponse(res, fileList);
            newItems.forEach((item, idx) => {
              const file = fileList[idx];
              if (file && typeof URL !== 'undefined' && URL.createObjectURL) {
                item.preview = URL.createObjectURL(file);
              }
            });
            const existing = values?.[name] || [];
            setFieldValue(name, [...existing, ...newItems]);
            if (newItems.length > 0) {
              showAlert(
                newItems.length === 1 ? 'File uploaded successfully' : `${newItems.length} files uploaded successfully`,
                'success'
              );
            }
            if (typeof onUploadSuccess === 'function') {
              onUploadSuccess(res?.data ?? res);
            }
          },
          onError: (err) => {
            const msg = err?.response?.data?.message
              ?? err?.response?.data?.Message
              ?? err?.message
              ?? 'Failed to upload attachment';
            showAlert(typeof msg === 'string' ? msg : 'Failed to upload attachment', 'error');
          }
        }
      );
      return;
    }

    const valid = fileList.map((file) => {
      const preview = showPreview && file.type.startsWith('image/')
        ? URL.createObjectURL(file) : null;
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
        preview
      };
    });
    if (valid.length > 0) {
      setFieldValue(name, [...formAttachments, ...valid]);
    }
  };

  const handleClickAdd = () => {
    if (effectiveDisabled) return;
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    onFilesSelected(e.target.files);
    e.target.value = null;
  };

  const openDeleteConfirm = (att) => {
    setAttachmentToDelete(att);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setAttachmentToDelete(null);
  };

  const openOrDownloadAttachment = (att) => {
    const url = att?.preSignedUrl ?? att?.preSignedURL;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (att?.preview) {
      window.open(att.preview, '_blank', 'noopener,noreferrer');
      return;
    }
    showAlert('No link available to open this attachment', 'error');
  };

  const confirmRemoveAttachment = () => {
    if (!attachmentToDelete) {
      closeDeleteModal();
      return;
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let serverId = attachmentToDelete.attachmentId ?? attachmentToDelete.id ?? attachmentToDelete.docId ?? attachmentToDelete.poAttachmentId;
    if (serverId == null || String(serverId).trim() === '') {
      const fromValues = Object.values(attachmentToDelete).find(
        (v) => typeof v === 'string' && uuidRegex.test(String(v).trim())
      );
      serverId = fromValues != null ? String(fromValues).trim() : serverId;
    }
    const hasServerId = serverId != null && String(serverId).trim() !== '';
    // Call delete API whenever attachment has a server id (docId), so backend actually deletes it.
    // Only remove from UI on success; if poId is missing we still call API with docId.
    const isServerAttachment = hasServerId;

    if (isServerAttachment) {
      removeAttachmentApi(
        {
          docId: serverId,
          attachmentId: serverId,
          purchaseOrderId: poId || undefined,
          ...(isArq && arqId != null && arqId !== '' && { arqId })
        },
        {
          onSuccess: () => {
            const updated = (formAttachments || []).filter(
              (a) => (a.attachmentId || a.id) !== serverId
            );
            setFieldValue(name, updated);
            showAlert('File deleted successfully', 'success');
            closeDeleteModal();
          },
          onError: (err) => {
            const msg = err?.response?.data?.message ?? err?.response?.data?.Message ?? err?.message ?? 'Failed to delete attachment';
            showAlert(typeof msg === 'string' ? msg : 'Failed to delete attachment', 'error');
            closeDeleteModal();
          }
        }
      );
    } else {
      if (attachmentToDelete.preview) {
        URL.revokeObjectURL(attachmentToDelete.preview);
      }
      const updated = (formAttachments || []).filter(
        (a) => a.id !== attachmentToDelete.id
      );
      setFieldValue(name, updated);
      showAlert('Image removed', 'success');
      closeDeleteModal();
    }
  };

  return (
    <div className="my-4">
      {showHeader && (
        <div
          onClick={() => setOpen(!open)}
          className="flex flex-row place-content-between mb-4  mt-4 bg-secondary-500 p-1 px-2.5 rounded-md  cursor-pointer">
          <div className="text-base font-muli text-white-light font-bold">{headerTitle}</div>
          <img src={polygon} alt="toggle" className={`${!open ? 'transform -rotate-90' : ''} w-6`} />
        </div>
      )}

      {open && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.csv,text/csv,.txt,text/plain,.msg,application/vnd.ms-outlook"
            multiple
            onChange={handleInputChange}
            style={{ display: 'none' }}
            disabled={effectiveDisabled}
          />
          {(isLoadingAttachments && poId) ? (
            <div className="text-sm text-gray-500">Loading attachments...</div>
          ) : (
            <div className="flex flex-nowrap gap-4 items-center overflow-x-auto">
              {showAddButton && (
                <button
                  type="button"
                  className="flex flex-shrink-0 items-center gap-3 p-3 rounded-md"
                  onClick={handleClickAdd}
                  disabled={effectiveDisabled || isUploading}
                  style={{ background: '#f6f7fb' }}
                >
                  <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center">
                    <img src={uploadFileIcon} alt="upload" className="w-9 h-9" />
                  </div>
                  <div className="text-primary-700 text-secondary-900">
                    {isUploading ? 'Uploading...' : 'Add Attachments'}
                  </div>
                </button>
              )}
              {(attachments || []).map((att) => (
                <div
                  key={att.id || att.attachmentId}
                  className="flex flex-shrink-0 items-center gap-3 p-2 bg-white rounded shadow-sm"
                  style={{ minWidth: 180 }}
                >
                  <div
                    className="w-12 h-12 bg-white-100 rounded overflow-hidden flex items-center justify-center cursor-pointer"
                    onClick={() => openOrDownloadAttachment(att)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openOrDownloadAttachment(att); }}
                    role="button"
                    tabIndex={0}
                    title="Open in new tab"
                  >
                    <img src={fileUpload} alt="uploaded" className="w-10 h-10" />
                  </div>
                  <div
                    className="flex-1 min-w-0 cursor-pointer hover:underline"
                    onClick={() => openOrDownloadAttachment(att)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openOrDownloadAttachment(att); }}
                    role="button"
                    tabIndex={0}
                    title="Open in new tab"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="text-sm font-muli text-gray-100 truncate"
                        style={{ maxWidth: 160 }}
                      >
                        {att.name}
                      </div>
                      {showPreview && att.preview ? (
                        <img
                          src={att.preview}
                          alt={`${att.name}-thumb`}
                          className="w-6 h-6 rounded"
                        />
                      ) : null}
                    </div>
                    <div className="text-xs text-gray-400">{humanFileSize(att.size)}</div>
                  </div>
                  {showDeleteButton && (
                    <button
                      type="button"
                      className="p-2"
                      disabled={effectiveDisabled || isDeleting}
                      onClick={() => openDeleteConfirm(att)}
                    >
                      <img src={closeIcon} alt="remove" className="w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={deleteModal}
        onClose={closeDeleteModal}
        center
        showCloseIcon={true}
        styles={{ modal: { width: '450px' } }}
      >
        <div className="p-4">
          <div className="text-lg font-semibold mb-3" style={{ color: 'rgba(248, 177, 19, 1)' }}>Delete attachment</div>
          <p className="text-gray-100 mb-6">
            Do you want to delete this attachment?
            {attachmentToDelete?.name && (
              <span className="block mt-1 text-sm font-semibold truncate">
                {attachmentToDelete.name}
              </span>
            )}
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="uppercase tracking-wide font-montserrat font-semibold max-w-57 px-6 rounded-lg   h-8 text-xm border-accent-100 border-2 hover:shadow-md outline-btn active:bg-accent-10 focus:outline-none focus:border-accent-100 text-gray-100"
              style={{ borderColor: 'rgba(248, 177, 19, 1)' }}
              onClick={closeDeleteModal}
            >
              CANCEL
            </button>
            <button
              type="button" 
              className="text-black uppercase tracking-wide font-montserrat font-semibold max-w-57 px-6 rounded-lg   h-8 text-xm   hover:shadow-md focus:outline-none fill-btn active:text-white-light bg-accent-100 text-gray-100 "
              style={{ backgroundColor: 'rgba(248, 177, 19, 1)' }}
              onClick={confirmRemoveAttachment}
              disabled={isDeleting}
            >
              {isDeleting ? 'DELETING...' : 'DELETE'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PoAttachments;
